import { ERROR_CODE_MESSAGES, STATUS_CODE_MESSAGES } from "./constants";
import type { ErrorCodeString, DisbursementApiErrorData, DisbursementErrorResponse } from "./types";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";

/**
 * Extracts and formats user-friendly error messages from API error responses.
 *
 * Priority order:
 * 1. Specific error_code from backend (e.g., INVALID_BU_CODE, INVALID_FILENAME_PATTERN)
 * 2. HTTP status code based message
 * 3. Generic fallback message
 *
 * For server errors (5xx), adds support contact message if backend message available.
 *
 * @param error - RTK Query error object
 * @param fileName - File name to include in error message
 * @returns Error message with title and description for toast display
 */
export const getDisbursementErrorMessage = (
  error: FetchBaseQueryError,
  fileName: string,
): DisbursementErrorResponse => {
  // Extract status code
  const statusCode = typeof error?.status === "number" ? error.status : 500;

  // Type guard to check if data matches our expected shape
  const errorData = error?.data as DisbursementApiErrorData | undefined;
  // Backend returns error code in 'error' field, not 'error_code'
  const errorCode = errorData?.error as ErrorCodeString | undefined;
  const backendMessage = errorData?.error_msg || errorData?.message;

  // Priority 1: Check for specific error code from backend
  if (errorCode && ERROR_CODE_MESSAGES[errorCode]) {
    const errorConfig = ERROR_CODE_MESSAGES[errorCode];

    const message = errorConfig.message;

    return {
      title: errorConfig.title,
      description: message,
    };
  }

  // Priority 2: Use status code based message
  if (STATUS_CODE_MESSAGES[statusCode]) {
    const errorConfig = STATUS_CODE_MESSAGES[statusCode];

    // For server errors (5xx), append backend message if available
    const message =
      statusCode >= 500 && backendMessage
        ? `${backendMessage} Please contact support if this error persists.`
        : errorConfig.message;

    return {
      title: errorConfig.title,
      description: `${message} File: ${fileName}`,
    };
  }

  // Priority 3: Generic fallback for unknown errors
  const fallbackMessage = backendMessage || "An unexpected error occurred";

  return {
    title: "Disbursement Failed",
    description: `${fallbackMessage}. Please contact support if this error persists. File: ${fileName}`,
  };
};
